import React, { useEffect } from 'react';
import { InputText } from 'primereact/inputtext';
import { Checkbox } from 'primereact/checkbox';
import { DAYS_OF_WEEK } from './constant.js';
import { getSubtypeHeader } from '../../../utils/subTypeHeader';

export default function BusinessHoursForm({ shopType, formState, setFormState }) {
  const subtype = shopType;
  const headerText = getSubtypeHeader(subtype);
  const businessHoursData = formState[`businessHours_${subtype}`] || {};

  const handleDayChange = (dayIndex, field, value) => {
    const newDays = [...(businessHoursData.days || Array(7).fill({}))];
    const dayName = DAYS_OF_WEEK[dayIndex]?.value || '';

    newDays[dayIndex] = {
      closingTime: newDays[dayIndex]?.closingTime || '17:00',  // Default to '17:00'
      dayName: dayName,
      open: field === 'open' ? value : (newDays[dayIndex]?.open || false),
      openingTime: newDays[dayIndex]?.openingTime || '09:00',  // Default to '09:00'
      ...newDays[dayIndex], // Keep existing properties
      [field]: value // Update the changed field
    };

    setFormState(`businessHours_${subtype}`, {
      ...businessHoursData,
      days: newDays
    });
  };

  useEffect(() => {
    if (businessHoursData.days) {
      const updatedDays = businessHoursData.days.map((day, index) => ({
        closingTime: day.closingTime || '17:00',  // Default to '17:00'
        dayName: day.dayName || DAYS_OF_WEEK[index]?.value || '',
        open: day.open !== undefined ? day.open : false,
        openingTime: day.openingTime || '09:00',  // Default to '09:00'
        ...day // Spread existing properties to preserve any additional fields
      }));

      setFormState(`businessHours_${subtype}`, {
        ...businessHoursData,
        days: updatedDays
      });
    }
  }, []);

  const handleBusinessHoursChange = (field, value) => {
    setFormState(`businessHours_${subtype}`, { ...businessHoursData, [field]: value });
  };

  const getDayLabel = (dayValue) => {
    return DAYS_OF_WEEK.find(day => day.value === dayValue)?.label || dayValue;
  };

  return (
    <div className="form-section p-4 bg-white rounded-lg shadow-sm">
      <h3 className="section-title text-lg font-semibold mb-4">Business Hours</h3>

      <div className="business-hours-container space-y-4">
        {DAYS_OF_WEEK.map((day, index) => {
          const dayData = businessHoursData.days?.[index] || {};
          return (
            <div key={day.value} className="business-hours-day border rounded-lg p-3">
              <div className="field mb-2">
                <div className="flex items-center">
                  <Checkbox
                    inputId={`open_${day.value}_${subtype}`}
                    checked={dayData.open || false}
                    onChange={(e) => handleDayChange(index, 'open', e.checked)}
                    className="mr-2"
                  />
                  <label htmlFor={`open_${day.value}_${subtype}`} className="text-gray-700">
                    {getDayLabel(day.value)}
                  </label>
                </div>
              </div>

              {dayData.open && (
                <div className="flex flex-col md:flex-row gap-3 items-start">
                  <div className="field flex-1">
                    <label className="block text-sm text-gray-600 mb-1">Opening Time</label>
                    <InputText
                      type="time"
                      value={dayData.openingTime}
                      onChange={(e) => handleDayChange(index, 'openingTime', e.target.value)}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="field flex-1">
                    <label className="block text-sm text-gray-600 mb-1">Closing Time</label>
                    <InputText
                      type="time"
                      value={dayData.closingTime}
                      onChange={(e) => handleDayChange(index, 'closingTime', e.target.value)}
                      className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <div className="flex items-center mb-3">
          <Checkbox
            inputId={`hasSpecialHours_${subtype}`}
            checked={businessHoursData.hasSpecialHours || false}
            onChange={(e) => handleBusinessHoursChange('hasSpecialHours', e.checked)}
            className="mr-2"
          />
          <label htmlFor={`hasSpecialHours_${subtype}`} className="text-gray-700">
            Has Special Hours (e.g., holidays, events)
          </label>
        </div>

        {businessHoursData.hasSpecialHours && (
          <div className="field">
            <label className="block text-sm text-gray-600 mb-1">Special Hours Description</label>
            <InputText
              value={businessHoursData.specialHoursDescription || ''}
              onChange={(e) => handleBusinessHoursChange('specialHoursDescription', e.target.value)}
              placeholder="e.g., Closed on Sundays, Special hours during holidays"
              className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}
      </div>

      <div className="mt-6">
        <div className="flex items-center mb-3">
          <Checkbox
            inputId={`hasBreakTime_${subtype}`}
            checked={businessHoursData.hasBreakTime || false}
            onChange={(e) => {
              const newValue = e.checked;
              const updates = { hasBreakTime: newValue };
              
              // Set default break times when enabling break time
              if (newValue) {
                updates.breakStart = businessHoursData.breakStart || '12:00';
                updates.breakEnd = businessHoursData.breakEnd || '13:00';
              }
              
              setFormState(`businessHours_${subtype}`, {
                ...businessHoursData,
                ...updates
              });
            }}
            className="mr-2"
          />
          <label htmlFor={`hasBreakTime_${subtype}`} className="text-gray-700">
            Has Break Time
          </label>
        </div>

        {businessHoursData.hasBreakTime && (
          <div className="flex flex-col md:flex-row gap-3 items-start">
            <div className="field flex-1">
              <label className="block text-sm text-gray-600 mb-1">Break Start Time</label>
              <InputText
                type="time"
                value={businessHoursData.breakStart || '12:00'}
                onChange={(e) => handleBusinessHoursChange('breakStart', e.target.value)}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="field flex-1">
              <label className="block text-sm text-gray-600 mb-1">Break End Time</label>
              <InputText
                type="time"
                value={businessHoursData.breakEnd || '13:00'}
                onChange={(e) => handleBusinessHoursChange('breakEnd', e.target.value)}
                className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
